import parse from "html-react-parser";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LuChevronDown, LuX } from "react-icons/lu";
import { useIsCompactView } from "../core_stores/useIsCompactViewStore";

interface FaqModalContextType {
  show: () => void;
}

const FaqModalContext = createContext<FaqModalContextType | undefined>(
  undefined
);

export function useFaqModal() {
  const ctx = useContext(FaqModalContext);
  if (!ctx)
    throw new Error(
      "usePrivacyTermsModal must be used within a PrivacyTermsModalProvider"
    );
  return ctx;
}

export function FaqModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isCompactView = useIsCompactView();

  useEffect(() => {
    // Prevent background scroll when modal is open
    if (isCompactView) {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  const show = useCallback(() => {
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  return (
    <FaqModalContext.Provider value={{ show }}>
      {children}
      {isOpen && (
        <div
          className={`${
            isClosing ? "animate-fade-out" : "animate-fade-in"
          } fixed inset-0
                flex items-center justify-center
                bg-black/25 backdrop-blur-sm
                transition-opacity duration-300 z-50`}
          onClick={handleClose}
        >
          <div
            className={`${
              isCompactView
                ? "h-full w-full"
                : "h-[90vh] max-h-[90vh] max-w-3xl rounded-lg m-8"
            } relative 
                  bg-light-surface-primary dark:bg-[#2B2E32]
                  shadow-[0_4px_2.6px_0_rgba(0,0,0,0.46),0_3px_14.2px_0_rgba(0,0,0,0.3)]
                  transform transition-all duration-300 ease-in-out
                  overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10
                    text-light-placeholder dark:text-dark-placeholder
                    hover:brightness-150 transition-all duration-300"
            >
              <LuX size={24} />
            </button>

            <div className="h-full flex flex-col">
              <div className="pb-4 flex-1 overflow-hidden py-8">
                <Content />
              </div>
            </div>
          </div>
        </div>
      )}
    </FaqModalContext.Provider>
  );
}

function Content() {
  const isCompactView = useIsCompactView();

  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0]));

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const faqData = [
    {
      question: "What is Darvin?",
      answer:
        "Darvin’s mission is to democratize mobile app development. Not web apps. Not wrappers. Real native mobile apps — the kind you can publish directly on Google Play and the Apple App Store. With Darvin, anyone can turn an idea into a native app — no coding, no hassle.",
    },
    {
      question: "What if I don’t like the first output I get from Darvin?",
      answer:
        "AI-generated results aren’t deterministic — the same prompt can produce different outcomes. This is especially true for UI design, where re-running or slightly rephrasing your prompt can give you new layouts, colors, or interaction styles. If the first build doesn’t match your expectations, try again or adjust your wording to guide the AI. Reruns can also change how features are structured or how the app’s flow is presented.",
    },
    {
      question: "How do tokens work?",
      answer: `Tokens are Darvin’s way of measuring usage. Different tasks use different amounts of tokens depending on their complexity — for example, generating your first app, making change requests, running web searches, or chatting with the AI.
<br/><br/>
For app builds, tokens cover the cloud infrastructure required to generate installable apps. You’re only charged tokens for completed builds. Each completed build has a fixed cost:
<br/><br/>
<ul style="list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0;">
  <li>20,000 tokens per Android APK build</li>
  <li>40,000 tokens per Android AAB build</li>
  <li>80,000 tokens per iOS IPA build</li>
  <li>40,000 tokens per Desktop build</li>
  <li>Web builds are always free.</li>
</ul>
<br/>
And to keep things smooth, <b>automatic bug fixes are always free</b> — so you won’t spend tokens when Darvin patches errors in your generated code.`,
    },
    {
      question: "Can I change my plan?",
      answer:
        'Yes, you can change your plan by clicking "Manage your payment preferences and check past payments". This will take you to our Stripe billing portal where you can select Update subscription to change your plan type.',
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer:
        "Absolutely. You can cancel whenever you want, and you’ll still have full access to your plan until the end of your billing cycle.",
    },
    {
      question: "How do I get the most out of Darvin?",
      answer: `You  don’t need to know code — but the way you describe your app really  matters. Clear, focused prompts help Darvin deliver results that match  your vision. Here are a few tips:
<br/>
<ul style="list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0;">
  <li><strong>Be specific</strong>: Describe the exact change you want, like "add email validation to the signup form."</li>
  <li><strong>Share context</strong>: Tell us what your app already does and what you’re aiming for.</li>
  <li><strong>Keep it step-by-step</strong>: Ask for one feature or change at a time for the best results.</li>
  <li><strong>Set constraints</strong>: Say if something must (or must not) be included, like "must work offline."</li>
  <li><strong>Highlight priorities</strong>: Let us know if design, performance, or simplicity matters most to you.</li>
  <li><strong>Show visual input</strong>: Upload a screenshot or sketch to guide style and layout.</li>
  <li><strong>Avoid vagueness</strong>: Replace “make it better” with clear actions like “center the button.”</li>
</ul>
Following these habits keeps vibe-coding smooth, reduces retries, and gets you closer to an app that feels just right.`,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="text-center mb-8 flex-shrink-0">
        <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
          Frequently Asked Questions
        </h1>
      </div>

      <div
        className={`flex-1 overflow-y-auto space-y-4 ${
          isCompactView ? "px-4" : "px-8"
        }`}
      >
        {faqData.map((item, index) => (
          <div key={index} className="rounded-lg overflow-hidden">
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left 
              bg-light-surface-secondary dark:bg-[#2B2E32] 
              hover:bg-light-surface-tertiary dark:hover:bg-[#191C1F] 
              transition-colors duration-300 
              flex items-center justify-between"
            >
              <span className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary pr-4">
                {item.question}
              </span>
              <LuChevronDown
                className={`text-light-text-secondary dark:text-dark-text-secondary flex-shrink-0
                    transition-transform duration-300
                  ${expandedItems.has(index) ? "-rotate-180" : ""}`}
                size={20}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                expandedItems.has(index)
                  ? "max-h-[1000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div
                className="px-6 py-4
                transform transition-transform duration-300"
                style={{
                  transform: expandedItems.has(index)
                    ? "translateY(0)"
                    : "translateY(-8px)",
                }}
              >
                <span className="text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
                  {parse(item.answer)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
